<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// Filtered agent list based on search
const filteredAgentList = computed(() => {
  if (!searchQuery.value) return agentList.value;
  const query = searchQuery.value.toLowerCase();
  return agentList.value.filter(
    agent =>
      agent.name.toLowerCase().includes(query) ||
      agent.email.toLowerCase().includes(query)
  );
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

const getStatusClass = agent => {
  if (agent.availability_status === 'online') return 'status-active';
  if (agent.availability_status === 'busy') return 'status-busy';
  return 'status-offline';
};

const getStatusLabel = agent => {
  if (agent.availability_status === 'online') return 'Active';
  if (agent.availability_status === 'busy') return 'Busy';
  return 'Offline';
};
</script>

<template>
  <div class="agents-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 56px"
    >
      <div class="flex items-center gap-2">
        <span class="i-ph-users-bold size-5" style="color: #6b7280" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('AGENT_MGMT.HEADER') }}
        </h1>
      </div>
      <div class="flex items-center gap-3">
        <!-- Stat pills -->
        <div class="ws-stat-pill">
          <span class="ws-stat-val">{{ agentList.length }}</span>
          <span class="ws-stat-lbl">Total</span>
        </div>
        <div class="ws-stat-pill" style="--pill-color: #16a34a">
          <span class="ws-stat-val" style="color: #16a34a">{{
            agentList.filter(a => a.availability_status === 'online').length
          }}</span>
          <span class="ws-stat-lbl">Online</span>
        </div>
        <button class="ws-btn" @click="openAddPopup">
          <span class="i-ph-plus-bold size-3.5" />
          {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
        </button>
      </div>
    </header>

    <!-- Description + Search toolbar -->
    <div class="ws-toolbar flex items-center gap-4 px-6 py-3">
      <div class="relative flex-1 max-w-xs">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
          style="color: #9ca3af"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="ws-search reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
          :placeholder="
            $t('AGENT_MGMT.SEARCH.PLACEHOLDER') || 'Search agents...'
          "
        />
      </div>
      <span class="text-sm" style="color: #6b7280">
        {{ filteredAgentList.length }}
        {{ $t('AGENT_MGMT.LIST.AGENTS') || 'agents' }}
      </span>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('AGENT_MGMT.LOADING')" />
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredAgentList.length" class="ws-empty">
        <div class="ws-empty-icon">
          <span class="i-ph-users-bold size-10" style="color: #9ca3af" />
        </div>
        <p style="color: #6b7280">{{ $t('AGENT_MGMT.LIST.404') }}</p>
      </div>

      <!-- Table -->
      <div v-else class="ws-table-wrap">
        <table class="ws-table">
          <thead class="ws-thead">
            <tr>
              <th class="ws-th">Name</th>
              <th class="ws-th">Status</th>
              <th class="ws-th">Role</th>
              <th class="ws-th">Verification</th>
              <th class="ws-th ws-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agent in filteredAgentList"
              :key="agent.email"
              class="ws-tr"
            >
              <!-- Name & Email -->
              <td class="ws-td">
                <div class="flex items-center gap-3">
                  <Avatar
                    :src="agent.thumbnail"
                    :name="agent.name"
                    :status="agent.availability_status"
                    :size="36"
                    hide-offline-status
                    rounded-full
                  />
                  <div>
                    <div
                      class="font-semibold"
                      style="color: #111827; font-size: 13px"
                    >
                      {{ agent.name }}
                    </div>
                    <div style="color: #6b7280; font-size: 12px">
                      {{ agent.email }}
                    </div>
                  </div>
                </div>
              </td>
              <!-- Status -->
              <td class="ws-td">
                <span class="ag-status-badge" :class="getStatusClass(agent)">{{
                  getStatusLabel(agent)
                }}</span>
              </td>
              <!-- Role -->
              <td class="ws-td" style="color: #374151">
                {{ getAgentRoleName(agent) }}
              </td>
              <!-- Verification -->
              <td class="ws-td">
                <span
                  v-if="agent.confirmed"
                  style="font-size: 12px; color: #16a34a; font-weight: 600"
                  >✓ {{ $t('AGENT_MGMT.LIST.VERIFIED') }}</span>
                <span
                  v-else
                  style="font-size: 12px; color: #d97706; font-weight: 600"
                  >⏳ {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}</span>
              </td>
              <!-- Actions -->
              <td class="ws-td ws-td--right">
                <div class="flex justify-end gap-2">
                  <Button
                    v-if="showEditAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                    icon="i-ph-pencil-simple-bold"
                    slate
                    xs
                    faded
                    @click="openEditPopup(agent)"
                  />
                  <Button
                    v-if="showDeleteAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-ph-trash-bold"
                    xs
                    ruby
                    faded
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modals -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>

<style scoped>
/* Forced white theme */
.agents-page,
.dark .agents-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  min-height: 56px;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}

.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}

/* Table */
.ws-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.ws-table {
  border-collapse: collapse;
  width: 100%;
}
.ws-thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.ws-th {
  padding: 10px 24px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb !important;
}
.ws-th--right {
  text-align: right;
}
.ws-tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}
.ws-tr:last-child {
  border-bottom: none;
}
.ws-tr:hover {
  background: #fafafa;
}
.ws-td {
  padding: 14px 24px;
  font-size: 13px;
  color: #111827;
  vertical-align: middle;
}
.ws-td--right {
  text-align: right;
}

/* Stat pills */
.ws-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  min-height: 34px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
}
.ws-stat-val {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}
.ws-stat-lbl {
  font-size: 11px;
  color: #6b7280;
  margin-top: 0;
  line-height: 1;
}

/* Button */
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-btn:active {
  transform: scale(0.98);
}

/* Empty state */
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

/* Status badges (light palette for white bg) */
.ag-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}
.status-active {
  background: #dcfce7;
  color: #16a34a;
}
.status-busy {
  background: #fef9c3;
  color: #ca8a04;
}
.status-offline {
  background: #f3f4f6;
  color: #6b7280;
}

/* Force add/edit modal surfaces to white */
:deep(.modal-mask) {
  background: rgba(15, 23, 42, 0.45) !important;
  backdrop-filter: blur(4px);
}

:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}

:deep(.modal-mask label) {
  color: #111827 !important;
}

:deep(.modal-mask input:not(.reset-base)),
:deep(.modal-mask select:not(.reset-base)),
:deep(.modal-mask textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
